import React, { useState, useEffect } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { DollarSign, Calendar, X, Save, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import moment from 'moment';

// InputField component defined outside to prevent recreating on each render
const InputField = ({ icon: Icon, label, name, type = 'text', required = false, value, onChange, showValidation, errors, ...props }) => (
  <div className="input-group">
    <label className="input-label">
      {label}
      {required && <span className="required-indicator">*</span>}
    </label>
    <div className="input-container">
      <div className="input-icon">
        <Icon size={16} />
      </div>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className={`modern-input ${showValidation && errors?.[name] ? 'error' : ''}`}
        {...props}
      />
    </div>
    {showValidation && errors?.[name] && (
      <div className="error-message">
        <AlertTriangle size={12} />
        <span>{errors[name]}</span>
      </div>
    )}
  </div>
);

const UpdateCompensationModal = ({ show, handleClose, employeeId, companyId, currentCompensation, currentContract, mode = 'create', onSuccess }) => {
  const isEditMode = mode === 'edit';
  const [formData, setFormData] = useState({
    amount_monthly: '',
    pay_frequency: 'monthly',
    valid_from: moment().format('YYYY-MM-DD'),
    currency: 'SEK'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [errors, setErrors] = useState({});

  // Update form data when modal is shown
  useEffect(() => {
    if (show) {
      if (isEditMode && currentCompensation) {
        // Pre-fill form with existing data when editing
        setFormData({
          amount_monthly: currentCompensation.amount_monthly || '',
          pay_frequency: currentCompensation.pay_frequency || 'monthly',
          valid_from: currentCompensation.valid_from || moment().format('YYYY-MM-DD'),
          currency: 'SEK'
        });
      } else {
        // Reset form for create mode
        // Use contract start date or today's date, whichever is later
        const today = moment().startOf('day');
        const contractStart = currentContract?.valid_from ? moment(currentContract.valid_from).startOf('day') : today;
        const defaultDate = moment.max(today, contractStart).format('YYYY-MM-DD');

        setFormData({
          amount_monthly: '',
          pay_frequency: 'monthly',
          valid_from: defaultDate,
          currency: 'SEK'
        });
      }
      // Reset validation states when modal opens
      setShowValidation(false);
      setErrors({});
      setError(null);
    }
  }, [show, isEditMode, currentCompensation, currentContract]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount_monthly || formData.amount_monthly <= 0) {
      newErrors.amount_monthly = 'Månadslön måste vara större än 0';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Datum måste anges';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Calculate minimum date for the date picker
  const getMinDate = () => {
    if (isEditMode) {
      return undefined; // No restriction when editing
    }

    const today = moment().startOf('day');
    const contractStart = currentContract?.valid_from ? moment(currentContract.valid_from).startOf('day') : today;

    // Use the later of today or contract start date
    return moment.max(today, contractStart).format('YYYY-MM-DD');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowValidation(true);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl(`${companyId}/hr/employees/${employeeId}/compensation`);

      const payload = {
        amount_monthly: parseFloat(formData.amount_monthly),
        pay_frequency: formData.pay_frequency,
        valid_from: formData.valid_from,
        currency: formData.currency
      };

      if (isEditMode) {
        // Use PUT to update existing compensation
        await axios.put(apiUrl, payload, { withCredentials: true });
      } else {
        // Use POST to create new compensation
        await axios.post(apiUrl, payload, { withCredentials: true });
      }

      if (onSuccess) onSuccess();
      resetAndClose();
    } catch (err) {
      console.error('Error updating compensation:', err);
      setError(err.response?.data?.message || 'Det gick inte att uppdatera ersättningen');
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFormData({
      amount_monthly: currentCompensation?.amount_monthly || '',
      pay_frequency: currentCompensation?.pay_frequency || 'monthly',
      valid_from: currentCompensation?.valid_from || moment().format('YYYY-MM-DD'),
      currency: 'SEK'
    });
    setError(null);
    setShowValidation(false);
    setErrors({});
    handleClose();
  };

  const SelectField = ({ icon: Icon, label, name, required = false, options, value, onChange, showValidation, errors, ...props }) => (
    <div className="input-group">
      <label className="input-label">
        {label}
        {required && <span className="required-indicator">*</span>}
      </label>
      <div className="input-container">
        <div className="input-icon">
          <Icon size={16} />
        </div>
        <select
          name={name}
          value={value}
          onChange={onChange}
          className={`modern-input ${showValidation && errors?.[name] ? 'error' : ''}`}
          {...props}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      {showValidation && errors?.[name] && (
        <div className="error-message">
          <AlertTriangle size={12} />
          <span>{errors[name]}</span>
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        .compensation-modal-content {
          border-radius: 12px;
          overflow: hidden;
          border: none;
        }
        
        .compensation-modal-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }
        
        .compensation-modal-body {
          padding: 2rem;
          background: #ffffff;
          max-height: 70vh;
          overflow-y: auto;
        }
        
        .close-button {
          position: absolute;
          top: 1rem;
          right: 1.5rem;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #64748b;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          cursor: pointer;
        }
        
        .close-button:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .form-section {
          margin-bottom: 2rem;
        }
        
        .section-title {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
        }
        
        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .input-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin: 0;
        }
        
        .required-indicator {
          color: #ef4444;
          margin-left: 0.25rem;
        }
        
        .input-container {
          position: relative;
          display: flex;
          align-items: center;
        }
        
        .input-icon {
          position: absolute;
          left: 0.75rem;
          z-index: 1;
          color: #6b7280;
          pointer-events: none;
        }
        
        .modern-input {
          width: 100%;
          padding: 0.75rem 0.75rem 0.75rem 2.5rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #374151;
          transition: all 0.2s ease;
        }
        
        .modern-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--rgba-primary-1);
        }
        
        .modern-input.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        .error-message {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #ef4444;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .current-value-card {
          background: #fef3c7;
          border: 1px solid #fbbf24;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .current-value-icon {
          color: #f59e0b;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .current-value-content {
          flex: 1;
        }
        
        .current-value-label {
          font-size: 0.75rem;
          color: #92400e;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }
        
        .current-value-amount {
          font-size: 1.25rem;
          font-weight: 700;
          color: #78350f;
          margin: 0;
        }
        
        .validation-summary {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .validation-icon {
          color: #ef4444;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .validation-content {
          flex: 1;
        }
        
        .validation-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: #dc2626;
          margin-bottom: 0.5rem;
        }
        
        .modal-actions {
          background: #f8fafc;
          padding: 1.5rem 2rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .action-button {
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        
        .btn-cancel {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        
        .btn-cancel:hover {
          background: #e2e8f0;
          color: #475569;
        }
        
        .btn-save {
          background: var(--bs-primary);
          color: white;
        }
        
        .btn-save:hover {
          background: var(--bs-primary);
          opacity: 0.9;
        }
        
        .btn-save:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }
        
        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-info {
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: start;
          gap: 0.75rem;
        }
        
        .info-icon {
          color: #0284c7;
          flex-shrink: 0;
          margin-top: 0.125rem;
        }
        
        .info-text {
          font-size: 0.875rem;
          color: #0c4a6e;
          margin: 0;
        }

        .info-list {
          list-style: none;
          padding: 0;
          margin: 0.5rem 0 0 0;
        }

        .info-list li {
          font-size: 0.75rem;
          color: #0c4a6e;
          margin-bottom: 0.25rem;
          padding-left: 1rem;
          position: relative;
        }

        .info-list li:before {
          content: '•';
          position: absolute;
          left: 0;
        }
        
        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          
          .modal-actions {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .action-button {
            justify-content: center;
          }
        }
      `}</style>

      <Modal show={show} onHide={resetAndClose} size="lg" backdrop="static" centered>
        <div className="compensation-modal-content">
          {/* Header */}
          <div className="compensation-modal-header">
            <button className="close-button" onClick={resetAndClose} aria-label="Stäng modal">
              <X size={16} />
            </button>
            <div className="d-flex align-items-center gap-3">
              <DollarSign size={24} color="#f59e0b" />
              <div>
                <h3 className="mb-1" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                  {isEditMode ? 'Redigera ersättning' : 'Skapa ny ersättning'}
                </h3>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '0.875rem' }}>
                  {isEditMode ? 'Uppdatera löneuppgifter för aktuell ersättning' : 'Registrera ny lön med giltighetsdatum'}
                </p>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="compensation-modal-body">
            <Form onSubmit={handleSubmit}>
              {/* Error message */}
              {error && (
                <div className="validation-summary">
                  <AlertTriangle size={16} className="validation-icon" />
                  <div className="validation-content">
                    <div className="validation-title">Ett fel uppstod</div>
                    <p style={{ fontSize: '0.875rem', color: '#dc2626', margin: 0 }}>{error}</p>
                  </div>
                </div>
              )}

              {/* Current value */}
              {currentCompensation && (
                <div className="current-value-card">
                  <TrendingUp size={20} className="current-value-icon" />
                  <div className="current-value-content">
                    <div className="current-value-label">Nuvarande månadslön</div>
                    <div className="current-value-amount">
                      {parseFloat(currentCompensation.amount_monthly).toLocaleString('sv-SE')} SEK
                    </div>
                  </div>
                </div>
              )}

              {/* Info message */}
              <div className="form-info">
                <CheckCircle size={16} className="info-icon" />
                <div className="info-text">
                  <strong>Automatiska beräkningar</strong>
                  <ul className="info-list">
                    <li>Timlön = (Månadslön × 12) ÷ (52 × arbetstimmar/vecka)</li>
                    <li>Daglön = Månadslön × 0.046</li>
                    <li>Veckolön = (Månadslön × 12) ÷ 52</li>
                  </ul>
                </div>
              </div>

              {/* Compensation Information */}
              <div className="form-section">
                <div className="section-title">
                  <DollarSign size={18} />
                  Löneuppgifter
                </div>

                <div className="form-grid">
                  <InputField
                    icon={DollarSign}
                    label="Ny månadslön"
                    name="amount_monthly"
                    type="number"
                    required={true}
                    placeholder="45000"
                    value={formData.amount_monthly}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    min="0"
                    step="0.01"
                  />

                  <SelectField
                    icon={TrendingUp}
                    label="Lönefrekvens"
                    name="pay_frequency"
                    required={true}
                    value={formData.pay_frequency}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    options={[
                      { value: 'monthly', label: 'Månadslön' },
                      { value: 'hourly', label: 'Timlön' },
                      { value: 'weekly', label: 'Veckolön' },
                      { value: 'daily', label: 'Daglön' }
                    ]}
                  />
                </div>
              </div>

              {/* Validity Period */}
              <div className="form-section">
                <div className="section-title">
                  <Calendar size={18} />
                  Giltighetstid
                </div>

                <div className="form-grid">
                  <InputField
                    icon={Calendar}
                    label="Gäller från"
                    name="valid_from"
                    type="date"
                    required={true}
                    value={formData.valid_from}
                    onChange={handleChange}
                    showValidation={showValidation}
                    errors={errors}
                    disabled={isEditMode}
                    min={getMinDate()}
                  />
                </div>
              </div>
            </Form>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              * Obligatoriska fält
            </div>
            <div className="d-flex gap-3">
              <button
                className="action-button btn-cancel"
                onClick={resetAndClose}
                disabled={loading}
              >
                Avbryt
              </button>
              <button
                className="action-button btn-save"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Sparar...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Spara ersättning
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default UpdateCompensationModal;
